<script setup>
import { computed, inject, ref } from 'vue';
import LeadFormModal from '@/Pages/Dashboard/Support/Partners/Partials/LeadFormModal.vue';
import PartnerServiceBox from '@/Pages/Dashboard/Support/Partners/Partials/PartnerServiceBox.vue';

const trans = inject('trans');

defineProps({
  partner: {
    type: Object,
    required: true,
  },
  applications: {
    type: Object,
    required: true,
  },
  leadTypes: {
    type: Array,
    required: true,
  },
});

const isShowLeadForm = ref(false);

const intakeMeetingDescription = computed(() => {
  return `${trans('__JSON__.FREE intake meeting of 15 minutes')}
  ${trans('__JSON__.Non-billable advice')}`;
});

const showLeadForm = () => {
  isShowLeadForm.value = true;
};
</script>

<script>
import DashboardLayout from '@/Layouts/DashboardLayout.vue';

export default {
  layout: DashboardLayout,
};
</script>

<template>
  <div class="partner-profile-wrapper">
    <div class="partner-profile-content">
      <div class="flex-row justify_start my-2 back-wrapper">
        <inertia-link class="back-link" :href="route('dashboard.support.partners.index')">
          {{ '<<' }} {{ trans('__JSON__.Back to partner overview') }}
        </inertia-link>
      </div>
      <div class="flex-row my-5">
        <h2>
          {{ trans('__JSON__.Profile') }} <span class="">{{ partner.companyName }}</span>
        </h2>
      </div>
      <div class="grid grid-3 m-5">
        <div class="flex-col">
          <div class="profile-info-header">
            <p>{{ trans('__JSON__.General information') }}</p>
          </div>
          <div v-if="partner.language.length > 0" class="profile-info">
            <p>{{ trans('__JSON__.Contact Language') }}</p>
            <p>{{ partner.language.join(', ') }}</p>
          </div>
          <div v-if="partner.applications.length > 0" class="profile-info">
            <p>{{ trans('__JSON__.Supported applications') }}</p>
            <p>{{ partner.applications.join(', ') }}</p>
          </div>
          <div v-if="partner.focusAreas.length > 0" class="profile-info">
            <p>{{ trans('__JSON__.Focus Areas') }}</p>
            <p>{{ partner.focusAreas.join(', ') }}</p>
          </div>
          <div v-if="partner.branches.length > 0" class="profile-info">
            <p>{{ trans('__JSON__.Branches') }}</p>
            <p>{{ partner.branches.join(', ') }}</p>
          </div>
          <div v-if="partner.website" class="profile-info">
            <p>{{ trans('__JSON__.Website') }}</p>
            <p>
              <a :href="partner.website" target="_blank">
                {{ partner.website }}
              </a>
            </p>
          </div>
        </div>
        <div class="flex-col">
          <div>
            <div class="profile-info-header">
              <p>{{ trans('__JSON__.More about') }} {{ partner.companyName }}</p>
            </div>
            <div class="profile-info">
              <p></p>
              <p>
                {{ partner.description }}
              </p>
            </div>
          </div>
          <div v-if="partner.certificates.length > 0" class="my-5">
            <div class="profile-info-header">
              <p>{{ trans('__JSON__.Certificates') }}</p>
            </div>
            <div class="profile-info flex flex-wrap gap">
              <template v-for="certificate in partner.certificates">
                <img :src="certificate" width="150" height="150" alt="" />
              </template>
            </div>
          </div>
        </div>
        <div>
          <div class="partner-profile-logo">
            <img :src="partner.logoUrl" :alt="`logo-for-${partner.companyName}`" />
          </div>
          <div class="text-center my-4">
            <button type="button" class="button" @click="showLeadForm">
              {{ trans('__JSON__.Contact partner') }}
            </button>
          </div>
        </div>
      </div>
      <div class="flex-row my-5">
        <h2>
          {{ trans('__JSON__.Services') }} <span class="">{{ partner.companyName }}</span>
        </h2>
        <div class="grid-4 gap">
          <partner-service-box
            v-if="partner.intakeMeetingLink"
            :title="trans('__JSON__.Free Intake Meeting')"
            :button-name="trans('__JSON__.Plan meeting')"
            :link="partner.intakeMeetingLink"
            :price="0"
            :description="intakeMeetingDescription"
            :is-price-monthly="false"
          />
          <partner-service-box
            v-for="(service, serviceIndex) in partner.packages"
            :key="`service-${serviceIndex}`"
            :title="service.name"
            :button-name="trans('__JSON__.Contact')"
            :link="service.link"
            :price="service.price"
            :description="service.description"
            :is-price-monthly="service.isPriceMonthly"
          />
        </div>
      </div>
    </div>
  </div>
  <lead-form-modal
    :partner="partner"
    :applications="applications"
    :lead-types="leadTypes"
    :show="isShowLeadForm"
    @close="isShowLeadForm = false"
  />
</template>

<style scoped lang="scss">
.partner-profile {
  &-content {
    width: 90%;
    margin: 0 auto;
    & h2 {
      font-size: 26px;
      font-weight: bold;
      line-height: 32px;
      font-style: normal;
      text-align: left;
      color: #5c5d5d;
      & > span {
        color: #2fabec;
      }
    }
  }
  &-wrapper {
    background: white;
    padding-top: 20px;
    padding-bottom: 20px;
  }
  &-logo {
    display: flex;
    justify-content: center;
    align-items: center;
    & img {
      width: 100%;
      border-radius: 10px;
      filter: drop-shadow(0px 2px 2.5px rgba(0, 122, 255, 0.09));
    }
  }
}
.back {
  &-wrapper {
    display: flex;
    justify-content: flex-start;
    gap: 20px;
  }
  &-link {
    font-size: 16px;
    font-weight: bold;
    line-height: 24px;
    font-style: normal;
    text-align: left;
    color: #9a9a9a;
    &:hover {
      color: #5c5d5d !important;
      text-decoration: none;
    }
  }
}
.profile-info {
  margin-top: 30px;
  margin-bottom: 30px;
  & p {
    font-size: 16px;
    line-height: 20px;
    color: #3f3f3f;
    font-style: normal;
    font-weight: 300;

    &:nth-child(1) {
      font-weight: bold;
      font-size: 18px;
      line-height: 40px;
      color: #5c5d5d;
      font-style: normal;
    }
  }
  &-header {
    & > p {
      font-size: 20px;
      font-weight: bold;
      line-height: 24px;
      color: #3f3f3f;
    }
  }
}
</style>
